import React, {useEffect, useState} from 'react';
import {Flex, Form, Input, message, Table, Upload} from 'antd';
import GreyButton from "../../../../components/UI/Buttons/GreyButton";
import IntlMessage, {IntlMessageText} from "../../../../components/IntlMessage/IntlMessage";
import MyText from "../../../../components/UI/Text/MyText";
import {clrs} from "../../../../constants/colors";
import AETService from "../../../../services/AETService";
import BlockLoading from "../../../../components/LoadingComponents/BlockLoading";
import debounce from "lodash/debounce";
import {getAllCertificates} from "../../../../store/slices/CertificateSlice";
import {useDispatch, useSelector} from "react-redux";
import {getAllAetResults} from "../../../../store/slices/AetResultsSlice";

const AituExcelTestPage = () => {
    const dispatch = useDispatch();

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(30);
    const [isUploadLoading, setIsUploadLoading] = useState(false);
    const {results, isLoading, hasMore, error} = useSelector(state => state.aetResultList)

    const [uploadedFiles, setUploadedFiles] = useState({
        english: [],
        math: [],
    });

    const [searchForm] = Form.useForm();
    const [searchParams, setSearchParams] = useState({ text: ''});

    useEffect(() => {
        const debouncedFetch = debounce(() => {
            dispatch(getAllAetResults({
                page: currentPage,
                limit: pageSize,
                ...searchParams,
            }));
        }, 500); // 500ms debounce

        debouncedFetch();

        return () => {
            debouncedFetch.cancel();
        };
    }, [searchParams, currentPage, pageSize]);

    useEffect(() => {
        setCurrentPage(1);
    }, [pageSize]);


    const handleUploadAll = async () => {
        const uploadTasks = [];
        setIsUploadLoading(true);
        for (const type of ['english', 'math']) {
            const file = uploadedFiles[type][0];
            const category = type === 'english' ? "General English" : "Mathematics";
            if (file) {
                uploadTasks.push(
                    AETService.loadAetResults(file, category)
                        .then(() => {
                            message.success(<IntlMessage id={'success'}/>);
                            setUploadedFiles(prev => ({...prev, [type]: []}));
                        })
                        .catch(() => {
                            message.error(<IntlMessage id={"error"}/>);
                        })
                        .finally(() => {
                            dispatch(getAllCertificates({
                                page: currentPage,
                                limit: pageSize,
                                ...searchParams,
                            }));
                        })
                );
            }
        }

        await Promise.all(uploadTasks).finally(() => {
            setIsUploadLoading(false);
        });
    };

    const handlePaginationChange = (page, pageSize) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    };

    const handleBeforeUpload = (type) => (file) => {
        const otherType = type === 'english' ? 'math' : 'english';

        if (uploadedFiles[otherType].length > 0) {
            message.warning(`Файл уже выбран для "${otherType.toUpperCase()}", сначала удалите его.`);
            return false;
        }

        setUploadedFiles(prev => ({
            ...prev,
            [type]: [file],
        }));
        return false;
    };


    const handleRemove = (type) => () => {
        setUploadedFiles(prev => ({
            ...prev,
            [type]: [],
        }));
    };


    const propsUpload = (type) => ({
        beforeUpload: handleBeforeUpload(type),
        onRemove: handleRemove(type),
        multiple: false,
        fileList: uploadedFiles[type],
        showUploadList: {
            showRemoveIcon: true,
            showDownloadIcon: false,
        },
    });


    const columns = [
        {
            title: <IntlMessage id={'fullName'}/>,
            render: (_, record) => <p>{record.fullName}</p>,
            width: '10%',
        },
        {
            title: <IntlMessage id={'iin'}/>,
            render: (_, record) => <p>{record.iin}</p>,
            width: '5%',
        },
        {
            title: <IntlMessage id={'telNumber'}/>,
            render: (_, record) => <p>{record.telNumber}</p>,
            width: '5%',
        },
        {
            title: <IntlMessage id={'subject'}/>,
            render: (_, record) => <p>{record.type}</p>,
            width: '15%',
        },
        {
            title: <IntlMessage id={'tg.grade'}/>,
            render: (_, record) => <p>{record.grade}</p>,
            width: '5%',
        },
    ];

    return (
        <Flex style={{width: "100%"}} gap={20} align={"flex-start"} justify={"flex-start"} vertical>
            <MyText uppercase strong size={"large"}><IntlMessage id={'aituExcelTest'}/></MyText>
            <Flex style={{width: "100%"}} align={"center"} justify={"center"} gap={20} vertical>
                <Flex gap={"middle"} align={"flex-start"} justify={"center"}>
                    <Upload {...propsUpload('english')}>
                        <GreyButton disabled={uploadedFiles.math.length > 0}><IntlMessage
                            id={'generalEnglish'}/></GreyButton>
                    </Upload>
                    <Upload {...propsUpload('math')}>
                        <GreyButton disabled={uploadedFiles.english.length > 0}><IntlMessage
                            id={'foundationPage.leftBlock.4'}/></GreyButton>
                    </Upload>
                </Flex>

                {(uploadedFiles.english.length > 0 || uploadedFiles.math.length > 0) && (
                    <GreyButton
                        onClick={handleUploadAll}
                        backgroundColor={clrs.blackerBlue}
                        fontColor={clrs.white}
                    >
                        <IntlMessage id="upload"/>
                    </GreyButton>
                )}
            </Flex>
            <Flex style={{width: "100%"}} align={"flex-start"} justify={"flex-start"}>
                <Form form={searchForm} layout="inline" style={{ marginBottom: 16 }}>
                    <Form.Item name="text">
                        <Input
                            placeholder={"Имя / ИИН"}
                            onChange={e => {
                                setSearchParams(prev => ({ ...prev, text: e.target.value }));
                                setCurrentPage(1);
                            }}
                        />
                    </Form.Item>
                </Form>
            </Flex>
            <BlockLoading isLoading={isLoading || isUploadLoading}/>
            <Table
                bordered
                columns={columns}
                dataSource={results}
                pagination={{
                    current: currentPage,
                    total: hasMore ? currentPage * pageSize + 1 : currentPage * pageSize,
                    defaultPageSize: pageSize,
                    showSizeChanger: true,
                    pageSize: pageSize,
                    pageSizeOptions: ['5', '10', '20', '30', '50', '100'],
                    onChange: handlePaginationChange,
                }}
                rowKey={record => record.id}
                loading={isLoading}
                style={{width: "100%"}}
                scroll={{x: 800}}
            />
        </Flex>
    );
};

export default AituExcelTestPage;
